import { Fragment, useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/shared/SortableTableHead';
import { SimplePagination } from '@/components/shared/SimplePagination';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  type GroupedAlert,
  OrganizationScanAlertsSortField,
} from '@kirin/api-client/contracts/alerts';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { ALERTS_PAGE_SIZE } from './constants';
import { AlertDetailContent } from './AlertDetailContent';
import { AlertUsersDialog } from './AlertUsersDialog';
import { UsersCell } from './UsersCell';
import {
  OutcomeCell,
  TITLE_COLUMN_WIDTH,
  TypeCell,
} from '@/components/DataTable/alerts/CellRenderers';
import type { AddFromAlertScope } from '@/hooks/api/useAddPolicyExceptionFromAlert';

/** Display-only column keys (no sort). */
export const FILE_PATH_FIELD = 'filePath' as const;
export const USERS_FIELD = 'users' as const;

export interface AlertsTableColumn<T extends string> {
  field: T | typeof FILE_PATH_FIELD | typeof USERS_FIELD;
  label: string;
  /** When false, column is display-only (e.g. File). Default true. */
  sortable?: boolean;
}

export interface AlertsTableProps<T extends string> {
  alerts: GroupedAlert[];
  pagination: { total: number; hasMore: boolean };
  page: number;
  onPrevious: () => void;
  onNext: () => void;
  sortBy: T;
  sortOrder: SortOrder;
  onSort: (field: T) => void;
  columns: AlertsTableColumn<T>[];
  /** Base path for building occurrences URL (e.g. /dashboard or /:organizationId). */
  alertsBasePath: string;
  /**
   * KIR-2788: when provided, "Show Instances" invokes this with the alertKey
   * instead of navigating to the occurrences route. Lets a caller (e.g. the
   * scoped notification landing) open the occurrences modal in place and keep
   * its own URL, so closing the modal returns to that view — not the alerts list.
   */
  onShowOccurrences?: (alertKey: string) => void;
  /**
   * KIR-4664: when set, the expanded-row {@link AlertDetailContent} is rendered
   * with the alert's `scope` + `alertKey` (and, via {@link detailOrganizationId},
   * `organizationId`), unlocking the exact "show more details" affordance the
   * main alerts page shows — the "Add to Allow/Block List" header action and the
   * org-scoped "View full scan result" link. Omitted (as in the legacy grouped
   * tab) → the card renders detail-only, unchanged.
   */
  detailScope?: AddFromAlertScope;
  /** KIR-4664: org id threaded into the expanded card for scan-result links. */
  detailOrganizationId?: string;
  /**
   * KIR-4664: when provided, an "Actions" column is rendered leading (right
   * after the expansion chevron) and this renders its per-row cell — the
   * shared Mute / Create Exclusion / Edit Policy / Add Comment controls. The
   * row-expansion click-through is suppressed for the cell here, so callers
   * only supply the buttons. Omitted → no Actions column (legacy grouped tab
   * layout is byte-identical).
   */
  renderRowActions?: (alert: GroupedAlert) => ReactNode;
}

function renderCell(
  field: string,
  alert: GroupedAlert,
  options: {
    onUsersClick?: (e: React.MouseEvent) => void;
    revampEnabled?: boolean;
  }
): React.ReactNode {
  const { onUsersClick, revampEnabled } = options;
  switch (field) {
    case OrganizationScanAlertsSortField.LastSeenAt:
    case 'lastSeenAt':
      return <RelativeTimeWithTooltip date={alert.lastSeenAt} />;
    case USERS_FIELD:
      return <UsersCell alert={alert} onClick={onUsersClick} />;
    case FILE_PATH_FIELD: {
      // KIR-2563: width-measured middle truncation, keeping the filename visible
      // and the full path copyable. The resolved, human-readable label is used
      // so nothing exposes the raw internal sentinel
      // (e.g. platform-secret:claude:PreToolUse).
      const fileLabel = getAlertFileDisplayLabel({
        filePath: alert.filePath,
        location: alert.location,
        eventKind: alert.eventKind,
      });
      // KIR-3810: alert File cells also get a one-click copy button.
      return <MiddleTruncatedPath path={fileLabel} showInlineCopy />;
    }
    case OrganizationScanAlertsSortField.Type:
    case 'type':
      // KIR-2381: render the shared `TypeCell` so the row label and the
      // expanded card label never disagree when the flag is on. KIR-4664: this
      // table is content-sized (w-max) with no column widths, so cap the cell
      // at the shared Title width or the two-line clamp never kicks in.
      return (
        <div style={{ maxWidth: TITLE_COLUMN_WIDTH }}>
          <TypeCell row={alert} revampEnabled={revampEnabled} />
        </div>
      );
    case OrganizationScanAlertsSortField.Severity:
    case 'severity':
      return <SeverityBadge riskLevel={alert.severity} showIcon />;
    case OrganizationScanAlertsSortField.OccurrenceCount:
    case 'occurrenceCount':
      return <span className="text-sm">{alert.occurrenceCount ?? '—'}</span>;
    case OrganizationScanAlertsSortField.Score:
    case 'score':
      return <span className="text-sm">{alert.score ?? '—'}</span>;
    case OrganizationScanAlertsSortField.FirstSeenAt:
    case 'firstSeenAt':
      return <RelativeTimeWithTooltip date={alert.firstSeenAt} />;
    case OrganizationScanAlertsSortField.Outcome:
    case 'outcome':
      return <OutcomeCell row={alert} />;
    default:
      return null;
  }
}

function AlertRow({
  alert,
  columns,
  alertsBasePath,
  onOpenUsersDialog,
  onShowOccurrences,
  detailScope,
  detailOrganizationId,
  renderRowActions,
}: {
  alert: GroupedAlert;
  columns: AlertsTableColumn<string>[];
  alertsBasePath: string;
  onOpenUsersDialog: (alert: GroupedAlert) => void;
  onShowOccurrences?: (alertKey: string) => void;
  detailScope?: AddFromAlertScope;
  detailOrganizationId?: string;
  renderRowActions?: (alert: GroupedAlert) => ReactNode;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showUserCount = columns.some(c => (c.field as string) === USERS_FIELD);
  // +1 for the leading chevron cell, +1 more when the Actions column is present.
  const colSpan = 1 + columns.length + (renderRowActions ? 1 : 0);
  // KIR-2381: hoist the flag read here (hooks must run unconditionally) and
  // thread `revampEnabled` into the non-React `renderCell` so row + expanded
  // labels stay aligned.
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  const occurrencesPath = `${alertsBasePath}/alerts/${encodeURIComponent(alert.alertKey)}/occurrences`;
  const occurrencesUrl = searchParams.toString()
    ? `${occurrencesPath}?${searchParams.toString()}`
    : occurrencesPath;

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(prev => !prev)}
      >
        <TableCell className="w-8">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        {renderRowActions && (
          // Suppress click-through to row expansion: the action controls own
          // their own click/focus semantics (mirrors the DataTable Actions
          // cell in the main alerts page).
          <TableCell
            data-column-id="actions"
            onClick={e => e.stopPropagation()}
            onPointerDown={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-start gap-2 pr-2">
              {renderRowActions(alert)}
            </div>
          </TableCell>
        )}
        {columns.map(col => (
          <TableCell
            key={col.field}
            className={
              col.field === USERS_FIELD ? 'text-sm text-center' : undefined
            }
            onClick={
              col.field === USERS_FIELD ? e => e.stopPropagation() : undefined
            }
          >
            {renderCell(col.field as string, alert, {
              onUsersClick: showUserCount
                ? (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onOpenUsersDialog(alert);
                  }
                : undefined,
              revampEnabled,
            })}
          </TableCell>
        ))}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell
            colSpan={colSpan}
            className="bg-muted/30 p-4 min-w-0 max-w-full w-full whitespace-normal"
          >
            <div className="min-w-0 max-w-full">
              {/* KIR-4481: AlertDetailContent is the single flag-gated wrapper
                  (grouped-cards redesign vs the legacy AlertDetailCard). KIR-4664:
                  thread the scoped-landing context (organizationId + scope +
                  alertKey) through so the expanded detail keeps its org-scoped
                  actions there — AlertDetailContent forwards them to whichever
                  panel it renders. */}
              <AlertDetailContent
                alert={alert}
                {...(detailOrganizationId
                  ? { organizationId: detailOrganizationId }
                  : {})}
                {...(detailScope
                  ? { scope: detailScope, alertKey: alert.alertKey }
                  : {})}
                onShowOccurrences={() => {
                  if (onShowOccurrences) {
                    onShowOccurrences(alert.alertKey);
                  } else {
                    navigate(occurrencesUrl);
                  }
                }}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Shared table for grouped alerts (user or organization).
 * Column order and labels are driven by columns; display-only columns use sortable: false.
 */
export function AlertsTable<T extends string>({
  alerts,
  pagination,
  page,
  onPrevious,
  onNext,
  sortBy,
  sortOrder,
  onSort,
  columns,
  alertsBasePath,
  onShowOccurrences,
  detailScope,
  detailOrganizationId,
  renderRowActions,
}: AlertsTableProps<T>): ReactElement {
  const [usersDialogAlert, setUsersDialogAlert] = useState<GroupedAlert | null>(
    null
  );

  return (
    <>
      <div className="rounded-md border">
        {/* KIR-4664: size the table to its content (w-max), min the container
            width — NOT table-fixed/w-full. Under table-fixed w-full the table
            was capped at the viewport width while the Action buttons overflowed
            beyond it, so a row's hover highlight and the expanded-section
            background stopped short of the scrolled-in Actions. Content-sizing
            makes every row (and the expanded cell) span the full scroll width. */}
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              {renderRowActions && <TableHead>Actions</TableHead>}
              {columns.map(col => (
                <Fragment key={col.field}>
                  {col.sortable === false ? (
                    <TableHead>{col.label}</TableHead>
                  ) : (
                    <SortableTableHead
                      field={col.field as T}
                      currentSortBy={sortBy}
                      currentSortOrder={sortOrder}
                      onSort={onSort}
                    >
                      {col.label}
                    </SortableTableHead>
                  )}
                </Fragment>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {alerts.map(alert => (
              <AlertRow
                key={alert.alertKey}
                alert={alert}
                columns={columns}
                alertsBasePath={alertsBasePath}
                onOpenUsersDialog={setUsersDialogAlert}
                onShowOccurrences={onShowOccurrences}
                detailScope={detailScope}
                detailOrganizationId={detailOrganizationId}
                renderRowActions={renderRowActions}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {usersDialogAlert && (
        <AlertUsersDialog
          alert={usersDialogAlert}
          open
          onClose={() => setUsersDialogAlert(null)}
        />
      )}
      <SimplePagination
        page={page}
        limit={ALERTS_PAGE_SIZE}
        total={pagination.total}
        hasMore={pagination.hasMore}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    </>
  );
}
